import { useMemo, useState } from "react";
import { getPublicFileUrl } from "@/lib/api";
import { usePublicLang } from "@/contexts/PublicLangContext";
import { getPriceBadge } from "@/lib/pricingUtils";
import ScrollReveal from "./ScrollReveal";
import CategoryPlaceholder from "./CategoryPlaceholder";
import TreatmentDetailModal from "./TreatmentDetailModal";
import PriceBadge from "@/components/PriceBadge";
import { LANDING } from "@/constants/testIds";

export default function TreatmentsGallery({ services, gallery = [], onBookService }) {
  const { t } = usePublicLang();
  const [activeCategory, setActiveCategory] = useState("all");
  const [detailService, setDetailService] = useState(null);

  const categories = useMemo(() => Array.from(new Set(services.map((s) => s.category).filter(Boolean))), [services]);
  const visible = activeCategory === "all" ? services : services.filter((s) => s.category === activeCategory);

  if (services.length === 0) return null;

  return (
    <section id="tratamientos" className="py-20 md:py-28 px-5 bg-white" data-testid={LANDING.treatments}>
      <div className="max-w-7xl mx-auto">
        <ScrollReveal className="text-center mb-10 md:mb-14">
          <h2 className="font-serif text-3xl md:text-5xl tracking-tight text-[#292522]">{t("landing.treatments.sectionTitle")}</h2>
        </ScrollReveal>

        {categories.length > 1 && (
          <ScrollReveal delay={0.1} className="flex items-center justify-center gap-2 overflow-x-auto pb-1 mb-8 md:mb-12">
            <button
              type="button"
              onClick={() => setActiveCategory("all")}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${activeCategory === "all" ? "bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]" : "bg-transparent text-[#6B6058] border-[rgba(168,149,130,0.28)] hover:border-[var(--brand-primary)]"}`}
              data-testid={`${LANDING.categoryTabPrefix}all`}
            >
              {t("landing.treatments.allCategories")}
            </button>
            {categories.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setActiveCategory(c)}
                className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${activeCategory === c ? "bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]" : "bg-transparent text-[#6B6058] border-[rgba(168,149,130,0.28)] hover:border-[var(--brand-primary)]"}`}
                data-testid={`${LANDING.categoryTabPrefix}${c}`}
              >
                {c}
              </button>
            ))}
          </ScrollReveal>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {visible.map((s, idx) => {
            const priceNote = getPriceBadge(s).note;
            return (
              <ScrollReveal key={s.id} delay={(idx % 3) * 0.08}>
                <div className="group w-full text-left" data-testid={`${LANDING.treatmentCardPrefix}${s.id}`}>
                  <button type="button" onClick={() => setDetailService(s)} className="w-full text-left block" data-testid={`${LANDING.treatmentCardOpenPrefix}${s.id}`}>
                    <div className="relative rounded-2xl overflow-hidden aspect-[4/5] mb-4">
                      {/* Fix AT (2026-10-27): el precio va en un sello redondo sobre la foto */}
                      <PriceBadge service={s} className="absolute top-3 right-3 z-10" testId={`${LANDING.treatmentPricePrefix}${s.id}`} />
                      {s.photo_url ? (
                        <img
                          src={getPublicFileUrl(s.photo_url)}
                          alt={s.name}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                      ) : (
                        <div className="w-full h-full transition-transform duration-700 group-hover:scale-105">
                          <CategoryPlaceholder category={s.category} name={s.name} />
                        </div>
                      )}
                    </div>
                    {s.category && <p className="text-xs uppercase tracking-[0.15em] font-medium text-[var(--brand-primary)] mb-1.5">{s.category}</p>}
                    <h3 className="font-serif text-xl md:text-2xl text-[#292522] mb-1">{s.name}</h3>
                    {s.summary && <p className="text-sm text-[#6B6058] line-clamp-2 mb-2">{s.summary}</p>}
                  </button>
                  {/* Fix E (2026-09-09) puso el precio como distintivo; Fix AT (2026-10-27) lo pasa al sello redondo de la foto.
                      Debajo queda solo la aclaracion del precio, si la tiene. */}
                  {priceNote && <p className="text-xs text-[#6B6058]" data-testid={`${LANDING.treatmentPriceNotePrefix}${s.id}`}>{priceNote}</p>}
                  <button
                    type="button"
                    onClick={() => onBookService(s)}
                    className="inline-block mt-3 text-xs font-semibold uppercase tracking-wide text-[#292522] border-b border-[#292522] hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)] transition-colors"
                    data-testid={`${LANDING.treatmentCtaPrefix}${s.id}`}
                  >
                    {t("landing.treatments.ctaBook")}
                  </button>
                </div>
              </ScrollReveal>
            );
          })}
        </div>
      </div>

      <TreatmentDetailModal service={detailService} gallery={gallery} onClose={() => setDetailService(null)} onBook={onBookService} />
    </section>
  );
}
